import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Loader2, ShieldAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Logo } from "@/components/layout/Logo";
import { LanguageToggle } from "@/components/language-toggle";
import { ThemeToggle } from "@/components/theme-toggle";
import { useAuth } from "@/context/AuthContext";

export const Route = createFileRoute("/login")({
  component: LoginPage,
  validateSearch: (search: Record<string, unknown>) => ({
    reason:
      search.reason === "expired" || search.reason === "error"
        ? (search.reason as "expired" | "error")
        : undefined,
  }),
});

/**
 * Manual login fallback. The auto-flow at `/` handles the happy path.
 * This page exists so that:
 *   - users landing on `/login` (e.g. after `signOut`) can re-trigger sign-in
 *   - sign-in failures redirect here with `?reason=error` for context
 *   - expired sessions redirect here with `?reason=expired`
 */
function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { reason } = Route.useSearch();
  const { t } = useTranslation();
  const [starting, setStarting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(
    reason === "expired"
      ? t("auth.session_expired")
      : reason === "error"
      ? t("auth.error_generic")
      : null,
  );

  useEffect(() => {
    if (auth.status === "authenticated") {
      void navigate({ to: "/dashboard", replace: true });
      return;
    }
    if (auth.status === "unauthenticated" && !reason && !errorMessage) {
      void handleSignIn();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.status]);

  const handleSignIn = async () => {
    setStarting(true);
    setErrorMessage(null);
    try {
      await auth.signIn("/dashboard");
    } catch (err) {
      console.error(err);
      setErrorMessage(t("auth.error_generic"));
      setStarting(false);
    }
  };

  return (
    <div className="relative flex min-h-screen flex-col bg-gradient-to-br from-background via-background to-primary/5">
      <header className="flex items-center justify-between px-6 pt-6">
        <Logo />
        <div className="flex items-center gap-1">
          <LanguageToggle />
          <ThemeToggle />
        </div>
      </header>
      <main className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center px-6 pb-16">
        <Card>
          <CardHeader>
            <CardTitle>{t("auth.signin_cta")}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <p className="text-sm text-muted-foreground">{t("app.tagline")}</p>
            {errorMessage && (
              <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                <ShieldAlert className="mt-0.5 h-4 w-4 flex-none" />
                <span>{errorMessage}</span>
              </div>
            )}
            <Button
              size="lg"
              onClick={handleSignIn}
              disabled={starting || auth.status === "loading"}
            >
              {starting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t("auth.signing_in")}
                </>
              ) : (
                t("auth.signin_cta")
              )}
            </Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}